import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { trackStoryFeedback, trackLessonFeedback } from '../../utils/analytics';

const wrapperStyle = {
  marginTop: 20,
  marginBottom: 16,
  paddingTop: 12,
  paddingBottom: 12,
  borderTop: '0.5px solid rgba(255,255,255,0.2)',
};

const labelStyle = {
  margin: '0 0 10px',
  fontSize: 13,
  color: 'rgba(255,255,255,0.75)',
};

const lessonWrapperStyle = {
  marginTop: 20,
  marginBottom: 16,
  paddingTop: 12,
  paddingBottom: 12,
  borderTop: '0.5px solid var(--color-border-tertiary, var(--color-border, #e5e7eb))',
};

const lessonLabelStyle = {
  margin: '0 0 10px',
  fontSize: 13,
  color: 'var(--color-text-secondary, #888)',
};

const thanksStyle = {
  margin: '10px 0 0',
  fontSize: 13,
  color: 'rgba(255,255,255,0.7)',
};

const lessonThanksStyle = {
  margin: '10px 0 0',
  fontSize: 13,
  color: 'var(--color-text-secondary, #888)',
};

function thumbBtnStyle(selected, faded) {
  return {
    flex: 1,
    minHeight: 44,
    borderRadius: 'var(--border-radius-md, 12px)',
    fontWeight: 600,
    fontSize: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    transition: 'opacity 0.2s, border-color 0.15s, background 0.15s',
    border: selected
      ? '1.5px solid rgba(0,0,0,0.4)'
      : '1.5px solid rgba(0,0,0,0.15)',
    background: selected ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    color: 'rgba(0,0,0,0.75)',
    opacity: faded ? 0.4 : 1,
    cursor: selected || faded ? 'default' : 'pointer',
    fontFamily: 'inherit',
  };
}

function starBtnStyle(filled) {
  return {
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    fontSize: 28,
    lineHeight: 1,
    color: filled ? 'var(--color-primary)' : 'var(--color-border, #ddd)',
    transition: 'color 0.12s',
  };
}

export default function QuickFeedback({
  type,
  contentId,
  character,
  worldId,
  totalCount,
  completedFully,
}) {
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);

  const handleStoryRate = (rating) => {
    if (locked) return;
    setSelected(rating);
    setLocked(true);
    trackStoryFeedback({
      storyId: contentId,
      character,
      rating,
      totalScenes: totalCount,
      completedFully,
    });
  };

  const handleLessonRate = (stars) => {
    if (locked) return;
    setSelected(stars);
    setLocked(true);
    trackLessonFeedback({
      lessonId: contentId,
      worldId,
      rating: stars,
      totalSteps: totalCount,
      completedFully,
    });
  };

  if (type === 'story') {
    const label = character
      ? `Did you enjoy ${character}'s adventure?`
      : 'Did you enjoy this story?';

    return (
      <div style={wrapperStyle}>
        <p style={labelStyle}>{label}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { id: 'up', emoji: '👍', text: 'Yes' },
            { id: 'down', emoji: '👎', text: 'Not for me' },
          ].map(({ id, emoji, text }) => (
            <motion.button
              key={id}
              type="button"
              whileTap={!locked ? { scale: 1.1 } : undefined}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
              onClick={() => handleStoryRate(id)}
              style={thumbBtnStyle(selected === id, locked && selected !== id)}
              disabled={locked}
            >
              {emoji} {text}
            </motion.button>
          ))}
        </div>
        {locked && <p style={thanksStyle}>Thanks! 💜</p>}
      </div>
    );
  }

  return (
    <div style={lessonWrapperStyle}>
      <p style={lessonLabelStyle}>How was this lesson?</p>
      <div style={{ display: 'flex' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            aria-label={`${star} star`}
            onClick={() => handleLessonRate(star)}
            disabled={locked}
            style={starBtnStyle(selected !== null && star <= selected)}
          >
            ★
          </button>
        ))}
      </div>
      {locked && <p style={lessonThanksStyle}>Thanks! 💜</p>}
    </div>
  );
}
